import {
  buildLearnedAutoFillPattern,
  createLearnedPatternSignature,
  decomposeFamilyNameToKeys,
  getLearnedRequiredKeysValidationError,
  hasLearnedAutoFillMappingValue,
  MAX_LEARNED_FAMILY_NAME_LENGTH,
  MAX_LEARNED_LIBRARY_ENTRIES,
  MAX_LEARNED_VALUE_LENGTH,
  type LearnedAutoFillPatternV1,
} from '../../../shared/lib/quickFill/learnedAutoFillLibrary';
import { autoFillFromFamilyName } from '../../../shared/lib/utils/autoFillService';
import type { QuickFillItem } from '../../../shared/types';

export interface QuickFillKeyLibraryDraftPreview {
  drafts: LearnedAutoFillPatternV1[];
  skippedRowCount: number;
  duplicateRowCount: number;
}

export type QuickFillKeyLibraryEditableField =
  | 'sourceFamilyName'
  | 'requiredKeys'
  | 'objectType'
  | 'skey'
  | 'end1'
  | 'end2'
  | 'end3'
  | 'end4';

export function createEmptyQuickFillKeyLibraryEntry(
  updatedAt = new Date().toISOString()
): LearnedAutoFillPatternV1 {
  return {
    id: '',
    sourceFamilyName: '',
    requiredKeys: [],
    values: { objectType: '', skey: '' },
    updatedAt,
  };
}

const KeyLibraryValueAliases = {
  'OBJECT-TYPE': ['OBJECT-TYPE', '#PCF_OBJECT_TYPE', 'OBJECT TYPE'],
  SKEY: ['SKEY', '#PCF_SKEY'],
  End1: ['End1', 'END-TYPE_1', 'END-TYPE-1', 'PiX_PPG_END1_TYPE'],
  End2: ['End2', 'END-TYPE_2', 'END-TYPE-2', 'PiX_PPG_END2_TYPE'],
  End3: ['End3', 'END-TYPE_3', 'END-TYPE-3', 'PiX_PPG_END3_TYPE'],
  End4: ['End4', 'END-TYPE_4', 'END-TYPE-4', 'PiX_PPG_END4_TYPE'],
} as const;

const normalizeMappedLabel = (value: string): string =>
  value.trim().toLocaleUpperCase().replace(/[^A-Z0-9]/g, '');

function readDisplayedMappedValue(
  item: QuickFillItem,
  aliases: readonly string[]
): string {
  const row = item as unknown as Record<string, unknown>;
  const availableValues = Object.entries(row).filter(
    ([key, value]) => !key.startsWith('__') && typeof value === 'string' && value.trim() !== ''
  );

  for (const alias of aliases) {
    const normalizedAlias = normalizeMappedLabel(alias);
    const match = availableValues.find(([key]) => normalizeMappedLabel(key) === normalizedAlias);
    if (match) {
      return String(match[1]);
    }
  }

  return '';
}

function projectDisplayedKeyLibraryValues(item: QuickFillItem): QuickFillItem {
  return {
    ...item,
    'OBJECT-TYPE': readDisplayedMappedValue(item, KeyLibraryValueAliases['OBJECT-TYPE']),
    SKEY: readDisplayedMappedValue(item, KeyLibraryValueAliases.SKEY),
    End1: readDisplayedMappedValue(item, KeyLibraryValueAliases.End1),
    End2: readDisplayedMappedValue(item, KeyLibraryValueAliases.End2),
    End3: readDisplayedMappedValue(item, KeyLibraryValueAliases.End3),
    End4: readDisplayedMappedValue(item, KeyLibraryValueAliases.End4),
  };
}

function createPendingEntryFingerprint(entry: LearnedAutoFillPatternV1): string {
  return [
    entry.id,
    entry.sourceFamilyName,
    entry.values.objectType,
    entry.values.skey,
    entry.values.end1 ?? '',
    entry.values.end2 ?? '',
    entry.values.end3 ?? '',
    entry.values.end4 ?? '',
  ].join('\u0000');
}

function collapseRepeatedDrafts(
  drafts: readonly LearnedAutoFillPatternV1[]
): LearnedAutoFillPatternV1[] {
  const unique = new Map<string, LearnedAutoFillPatternV1>();
  drafts.forEach((entry) => {
    const fingerprint = createPendingEntryFingerprint(entry);
    if (!unique.has(fingerprint)) {
      unique.set(fingerprint, entry);
    }
  });
  return Array.from(unique.values());
}

export function buildQuickFillKeyLibraryDraftPreview(
  quickFillData: readonly QuickFillItem[],
  selectedRows: readonly number[],
  updatedAt = new Date().toISOString()
): QuickFillKeyLibraryDraftPreview {
  const candidates = selectedRows.map((index) =>
    buildLearnedAutoFillPattern(
      projectDisplayedKeyLibraryValues(quickFillData[index] ?? { Name: '' }),
      updatedAt
    )
  );
  const completeDrafts = candidates.filter(
    (entry): entry is LearnedAutoFillPatternV1 => entry !== null
  );
  const drafts = collapseRepeatedDrafts(completeDrafts);

  return {
    drafts,
    skippedRowCount: candidates.length - completeDrafts.length,
    duplicateRowCount: completeDrafts.length - drafts.length,
  };
}

export function updateQuickFillKeyLibraryEntry(
  entries: readonly LearnedAutoFillPatternV1[],
  index: number,
  field: QuickFillKeyLibraryEditableField,
  value: string,
  updatedAt = new Date().toISOString()
): LearnedAutoFillPatternV1[] {
  return entries.map((entry, entryIndex) => {
    if (entryIndex !== index) {
      return entry;
    }

    if (field === 'requiredKeys') {
      const requiredKeys = decomposeFamilyNameToKeys(value);
      return {
        ...entry,
        id: createLearnedPatternSignature(requiredKeys),
        requiredKeys,
        updatedAt,
      };
    }

    if (field === 'sourceFamilyName') {
      return { ...entry, sourceFamilyName: value, updatedAt };
    }

    return {
      ...entry,
      values: { ...entry.values, [field]: value },
      updatedAt,
    };
  });
}

export function getQuickFillKeyLibraryValidationError(
  entries: readonly LearnedAutoFillPatternV1[]
): string | undefined {
  if (entries.length > MAX_LEARNED_LIBRARY_ENTRIES) {
    return `A dictionary can contain at most ${MAX_LEARNED_LIBRARY_ENTRIES.toLocaleString()} entries.`;
  }

  for (const entry of entries) {
    if (!entry.sourceFamilyName.trim()) {
      return 'Every entry needs a family name.';
    }
    if (entry.sourceFamilyName.length > MAX_LEARNED_FAMILY_NAME_LENGTH) {
      return `Family names must be ${MAX_LEARNED_FAMILY_NAME_LENGTH} characters or fewer.`;
    }

    const keysError = getLearnedRequiredKeysValidationError(entry.requiredKeys);
    if (keysError) {
      return keysError;
    }
    if (!hasLearnedAutoFillMappingValue(entry.values)) {
      return 'Every entry needs at least one mapping value.';
    }
    if (Object.values(entry.values).some((value) => value.length > MAX_LEARNED_VALUE_LENGTH)) {
      return `Mapping values must be ${MAX_LEARNED_VALUE_LENGTH} characters or fewer.`;
    }
  }

  const uniqueIds = new Set(entries.map((entry) => entry.id));
  return uniqueIds.size === entries.length
    ? undefined
    : 'Required key combinations must be unique before saving.';
}

export function findBuiltInShadowedPatternIds(
  drafts: readonly LearnedAutoFillPatternV1[]
): ReadonlySet<string> {
  return new Set(
    drafts
      .filter((entry) => autoFillFromFamilyName(
        entry.sourceFamilyName,
        { includeLearned: false }
      ).confidence > 0)
      .map((entry) => entry.id)
  );
}
